import type { ComponentProps, ReactNode } from 'react';
import type { AnyFieldApi } from '@tanstack/react-form';

import { Checkbox } from '@/shared/ui/shadcn/checkbox';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/shared/ui/shadcn/field';
import { Input } from '@/shared/ui/shadcn/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { getFieldState } from '@/shared/lib/forms';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

interface FormLike<TName extends string> {
  Field: React.ComponentType<{
    name: TName;
    children: (field: AnyFieldApi) => ReactNode;
  }>;
}

interface BaseProps<TForm, TName extends string> {
  form: TForm & FormLike<TName>;
  name: TName;
  label: string;
  description?: string;
}

interface TextProps<TForm, TName extends string> extends BaseProps<TForm, TName> {
  type: 'text' | 'password';
  placeholder?: string;
  inputProps?: Omit<ComponentProps<typeof Input>, 'name' | 'value' | 'onBlur' | 'onChange' | 'id'>;
  parse?: (raw: string) => string;
}

interface TextAreaProps<TForm, TName extends string> extends BaseProps<TForm, TName> {
  type: 'textarea';
  placeholder?: string;
  textAreaProps?: Omit<
    ComponentProps<typeof Textarea>,
    'name' | 'value' | 'onBlur' | 'onChange' | 'id'
  >;
}

interface SelectProps<TForm, TName extends string> extends BaseProps<TForm, TName> {
  type: 'select';
  options: SelectOption[];
  placeholder?: string;
  triggerClassName?: string;
  contentProps?: Omit<ComponentProps<typeof SelectContent>, 'children'>;
  afterTrigger?: ReactNode;
}

interface CheckboxProps<TForm, TName extends string> extends BaseProps<TForm, TName> {
  type: 'checkbox';
  checkboxProps?: Omit<
    ComponentProps<typeof Checkbox>,
    'name' | 'checked' | 'onCheckedChange' | 'onBlur' | 'id'
  >;
}

export type FormFieldProps<TForm, TName extends string> =
  | TextProps<TForm, TName>
  | TextAreaProps<TForm, TName>
  | SelectProps<TForm, TName>
  | CheckboxProps<TForm, TName>;

/**
 * Generic form field component for TanStack Form + shadcn/ui.
 * Supports text, password, textarea, select, and checkbox inputs.
 */
export function FormField<TForm, TName extends string>(props: FormFieldProps<TForm, TName>) {
  const { form, name, label, description, type } = props;
  const Form = form as FormLike<TName>;

  return (
    <FieldGroup>
      <Form.Field
        name={name}
        children={(field) => {
          const { isInvalid, errors } = getFieldState(field.state.meta);

          if (type === 'checkbox') {
            const { checkboxProps } = props as CheckboxProps<TForm, TName>;
            return (
              <Field orientation="horizontal" data-invalid={isInvalid}>
                <Checkbox
                  id={field.name}
                  name={field.name}
                  checked={Boolean(field.state.value)}
                  onBlur={field.handleBlur}
                  onCheckedChange={(v) => field.handleChange(Boolean(v))}
                  aria-invalid={isInvalid}
                  {...checkboxProps}
                />
                <FieldContent>
                  <FieldLabel htmlFor={field.name} className="font-medium">
                    {label}
                  </FieldLabel>
                  {description ? <FieldDescription>{description}</FieldDescription> : null}
                  {isInvalid ? <FieldError errors={errors} /> : null}
                </FieldContent>
              </Field>
            );
          }

          if (type === 'select') {
            const {
              options,
              placeholder = 'Select',
              triggerClassName,
              contentProps,
              afterTrigger,
            } = props as SelectProps<TForm, TName>;
            const groupedOptions = options.reduce<Array<[string | null, SelectOption[]]>>(
              (groups, option) => {
                const key = option.group ?? null;
                const existing = groups.find(([group]) => group === key);
                if (existing) {
                  existing[1].push(option);
                } else {
                  groups.push([key, [option]]);
                }
                return groups;
              },
              []
            );
            const hasGroups = groupedOptions.some(([group]) => group !== null);

            return (
              <Field orientation="responsive" data-invalid={isInvalid}>
                <FieldContent>
                  {label.length > 0 ? <FieldLabel htmlFor={field.name}>{label}</FieldLabel> : null}
                  {description ? <FieldDescription>{description}</FieldDescription> : null}
                  {isInvalid ? <FieldError errors={errors} /> : null}
                </FieldContent>
                <div className="flex w-full flex-col gap-2 @md/field-group:w-auto">
                  <Select
                    name={field.name}
                    value={(field.state.value as string) ?? ''}
                    onValueChange={(v) => field.handleChange(v)}
                  >
                    <SelectTrigger
                      id={field.name}
                      aria-invalid={isInvalid}
                      className={triggerClassName}
                    >
                      <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent position="item-aligned" {...contentProps}>
                      {hasGroups
                        ? groupedOptions.map(([group, groupOptions], groupIndex) => (
                            <SelectGroup key={group ?? 'other'}>
                              {group ? <SelectLabel>{group}</SelectLabel> : null}
                              {groupOptions.map((o) => (
                                <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                                  {o.label}
                                </SelectItem>
                              ))}
                              {groupIndex < groupedOptions.length - 1 ? <SelectSeparator /> : null}
                            </SelectGroup>
                          ))
                        : options.map((o) => (
                            <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                              {o.label}
                            </SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                  {afterTrigger}
                </div>
              </Field>
            );
          }

          if (type === 'textarea') {
            const { placeholder, textAreaProps } = props as TextAreaProps<TForm, TName>;
            return (
              <Field data-invalid={isInvalid}>
                <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
                <Textarea
                  id={field.name}
                  name={field.name}
                  value={(field.state.value as string) ?? ''}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  aria-invalid={isInvalid}
                  placeholder={placeholder}
                  {...textAreaProps}
                />
                {description ? <FieldDescription>{description}</FieldDescription> : null}
                {isInvalid ? <FieldError errors={errors} /> : null}
              </Field>
            );
          }

          // text or password
          const { placeholder, inputProps, parse } = props as TextProps<TForm, TName>;
          return (
            <Field data-invalid={isInvalid}>
              <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                type={type}
                value={(field.state.value as string) ?? ''}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  const raw = e.target.value;
                  field.handleChange(parse ? parse(raw) : raw);
                }}
                aria-invalid={isInvalid}
                placeholder={placeholder}
                autoComplete="off"
                {...inputProps}
              />
              {description ? <FieldDescription>{description}</FieldDescription> : null}
              {isInvalid ? <FieldError errors={errors} /> : null}
            </Field>
          );
        }}
      />
    </FieldGroup>
  );
}
